import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  FileCheck2,
  LockKeyhole,
  School,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import Button from "../components/Button";
import { Blob, Dots, Star } from "../components/Decor";
import AccentRays from "../components/decor/AccentRays";
import HandDrawnUnderline from "../components/decor/HandDrawnUnderline";
import { mergeLocalRecord, startDemoSession } from "../lib/demoAuth";
import brandLogo from "../assets/images/logo-fix.png";
import SEO from "../components/SEO";

const steps = [
  { number: 1, label: "Akun Perwakilan", icon: UserRound },
  { number: 2, label: "Informasi Sekolah", icon: Building2 },
  { number: 3, label: "Verifikasi / Review", icon: FileCheck2 },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  position: "",
  password: "",
  confirmPassword: "",
  schoolName: "",
  schoolLevel: "",
  city: "",
  address: "",
  curriculum: "",
};

const inputClass =
  "min-h-12 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm font-medium text-ink outline-none transition placeholder:text-ink/35 focus:border-purple focus:ring-4 focus:ring-purple/10";

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-ink">{label}</span>
      {children}
      {error ? <span className="mt-1.5 block text-xs font-semibold text-coral">{error}</span> : null}
    </label>
  );
}

function Progress({ currentStep }) {
  return (
    <ol className="grid grid-cols-3" aria-label="Tahapan pendaftaran">
      {steps.map((item, index) => {
        const complete = currentStep > item.number;
        const active = currentStep === item.number;
        return (
          <li key={item.number} className="relative flex flex-col items-center text-center">
            {index > 0 ? (
              <span
                className={`absolute right-1/2 top-5 h-0.5 w-full ${currentStep > index ? "bg-mint" : "bg-ink/12"}`}
                aria-hidden="true"
              />
            ) : null}
            <span
              className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-extrabold transition-colors ${
                complete
                  ? "border-mint bg-mint text-ink"
                  : active
                    ? "border-ink bg-yellow text-ink"
                    : "border-ink/15 bg-white text-ink/45"
              }`}
              aria-current={active ? "step" : undefined}
            >
              {complete ? <Check className="h-5 w-5" aria-hidden="true" /> : item.number}
            </span>
            <span className={`mt-2 text-xs font-bold sm:text-sm ${active ? "text-ink" : "text-ink/50"}`}>
              {item.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="border-b border-ink/10 py-3 last:border-0">
      <dt className="text-xs font-bold uppercase text-ink/45">{label}</dt>
      <dd className="mt-1 break-words text-sm font-semibold text-ink">{value || "Belum diisi"}</dd>
    </div>
  );
}

function BackgroundDecor() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <Dots className="absolute -left-1 top-24 h-20 w-20 opacity-25 sm:left-6" color="#73DCC6" />
      <Star className="absolute right-[5%] top-24 h-6 w-6 opacity-70" color="#6554E8" />
      <Blob className="absolute -bottom-44 -right-36 h-[420px] w-[420px] opacity-20" color="#73DCC6" />
      <Blob className="absolute -left-40 top-[48%] h-80 w-80 opacity-15" color="#FFD966" />
    </div>
  );
}

export default function SchoolRegistration() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [completed, setCompleted] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validateStep = () => {
    const nextErrors = {};

    if (step === 1) {
      if (!form.fullName.trim()) nextErrors.fullName = "Nama lengkap perlu diisi.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Masukkan alamat email yang valid.";
      if (!form.phone.trim()) nextErrors.phone = "Nomor telepon perlu diisi.";
      if (!form.position) nextErrors.position = "Pilih jabatan Anda di sekolah.";
      if (form.password.length < 8) nextErrors.password = "Kata sandi minimal 8 karakter.";
      if (form.confirmPassword !== form.password) nextErrors.confirmPassword = "Konfirmasi kata sandi belum sama.";
    }

    if (step === 2) {
      if (!form.schoolName.trim()) nextErrors.schoolName = "Nama sekolah perlu diisi.";
      if (!form.schoolLevel) nextErrors.schoolLevel = "Pilih jenjang sekolah.";
      if (!form.city.trim()) nextErrors.city = "Kota atau kabupaten perlu diisi.";
      if (!form.address.trim()) nextErrors.address = "Alamat sekolah perlu diisi.";
      if (!form.curriculum.trim()) nextErrors.curriculum = "Kurikulum perlu diisi.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = (event) => {
    event.preventDefault();
    if (!validateStep()) return;
    setStep((current) => Math.min(3, current + 1));
  };

  const reviewGroups = useMemo(
    () => [
      {
        title: "Akun Perwakilan",
        items: [
          ["Nama", form.fullName],
          ["Email", form.email],
          ["Nomor telepon", form.phone],
          ["Jabatan", form.position],
        ],
      },
      {
        title: "Informasi Sekolah",
        items: [
          ["Nama sekolah", form.schoolName],
          ["Jenjang", form.schoolLevel],
          ["Kota/Kabupaten", form.city],
          ["Kurikulum", form.curriculum],
          ["Alamat", form.address],
        ],
      },
    ],
    [form]
  );

  const finishRegistration = () => {
    if (!accepted) return;
    setCompleted(true);
    startDemoSession({ role: "school", name: form.fullName, email: form.email });
    mergeLocalRecord("msm_school_representative", {
      name: form.fullName,
      fullName: form.fullName,
      email: form.email,
      phone: form.phone,
      position: form.position,
    });
    try {
      localStorage.setItem(
        "msm_school_profile",
        JSON.stringify({
          id: "school-profile",
          slug: "",
          name: form.schoolName,
          educationLevels: form.schoolLevel ? [form.schoolLevel] : [],
          city: form.city,
          district: "",
          address: form.address,
          description: "",
          accreditation: "",
          curriculum: form.curriculum,
          images: [],
          facilities: [],
          extracurriculars: [],
          interests: [],
          completed: false,
          updatedAt: null,
          activities: [],
        })
      );
    } catch {
      /* The dashboard still opens with in-memory fallbacks. */
    }
    navigate("/dashboard/sekolah", { replace: true });
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-warm px-4 py-6 sm:px-6 lg:px-8">
      <SEO title="Daftar Sekolah | MySchoolMate" canonicalPath="/daftar/sekolah" noIndex />
      <BackgroundDecor />

      <div className="relative z-10 mx-auto max-w-[1320px]">
        <header className="flex items-center py-2 sm:py-3">
          <Link to="/" className="rounded-lg focus-ring" aria-label="Kembali ke beranda MySchoolMate">
            <img src={brandLogo} alt="MySchoolMate" width="288" height="43" className="h-8 w-auto sm:h-9" />
          </Link>
        </header>

        <div className="mt-6 grid items-stretch gap-6 lg:grid-cols-[0.68fr_1fr] lg:gap-8 xl:gap-10">
          <aside className="relative flex flex-col overflow-hidden rounded-[30px] border border-ink/10 bg-[#F2FBF8] p-7 sm:p-9 lg:min-h-[700px] lg:p-10">
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 rounded-full border border-mint/60 bg-white/75 px-3 py-2 text-xs font-extrabold uppercase text-ink">
                <School className="h-4 w-4 text-mint" aria-hidden="true" />
                Perwakilan Sekolah
              </span>

              <div className="relative mt-7">
                <AccentRays
                  variant="triple"
                  color="yellow"
                  rotation={12}
                  className="absolute -right-1 -top-8 h-10 w-10 sm:right-8"
                />
                <h1 className="max-w-md text-4xl font-extrabold leading-[1.12] text-ink sm:text-5xl">
                  Daftarkan sekolah{" "}
                  <span className="relative inline-block">
                    Anda
                    <HandDrawnUnderline
                      variant="brush"
                      color="mint"
                      className="absolute -bottom-3 left-0 h-4 w-full"
                    />
                  </span>
                </h1>
              </div>

              <p className="mt-6 max-w-md font-medium leading-relaxed text-ink/68">
                Lengkapi informasi perwakilan dan sekolah agar profil dapat disusun dengan lebih jelas di MySchoolMate.
              </p>

              <ul className="mt-8 space-y-4">
                {[
                  "Kelola informasi profil sekolah",
                  "Tampilkan program, fasilitas, dan biaya",
                  "Perbarui informasi ketika kebutuhan sekolah berubah",
                ].map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3 text-sm font-semibold text-ink/75">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-mint/25 text-ink">
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="pt-1">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mt-auto pt-10">
              <div className="overflow-hidden rounded-[24px] h-48 sm:h-64 w-full">
                <img
                  src="/assets/kepala-sekolah.png"
                  alt="Ilustrasi kepala sekolah"
                  width="760"
                  height="475"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </aside>

          <section className="rounded-[30px] border border-ink/12 bg-white p-5 shadow-soft sm:p-8 lg:p-10">
            <Progress currentStep={step} />

            <div className="mt-9 border-t border-ink/10 pt-8">
              {completed ? (
                <div className="flex min-h-[480px] flex-col items-center justify-center text-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-[24px] bg-mint/25 text-ink">
                    <CheckCircle2 className="h-10 w-10" aria-hidden="true" />
                  </span>
                  <h2 className="mt-6 text-3xl font-extrabold text-ink">Informasi siap ditindaklanjuti</h2>
                  <p className="mt-4 max-w-lg font-medium leading-relaxed text-ink/65">
                    Data sudah ditinjau di halaman ini. Pengiriman pendaftaran belum terhubung ke server pada versi saat ini.
                  </p>
                  <Button to="/signup" variant="secondary" className="mt-8 min-h-12">
                    Kembali ke pilihan peran
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </div>
              ) : null}

              {!completed && step === 1 ? (
                <form onSubmit={handleNext} noValidate>
                  <div>
                    <p className="text-xs font-extrabold uppercase text-purple">Langkah 1 dari 3</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">Informasi Akun Perwakilan</h2>
                    <p className="mt-2 text-sm font-medium text-ink/60">Gunakan informasi orang yang akan mengelola profil sekolah.</p>
                  </div>

                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <Field label="Nama Lengkap" error={errors.fullName}>
                      <input name="fullName" value={form.fullName} onChange={updateField} className={inputClass} placeholder="Masukkan nama lengkap" autoComplete="name" />
                    </Field>
                    <Field label="Jabatan di Sekolah" error={errors.position}>
                      <select name="position" value={form.position} onChange={updateField} className={inputClass}>
                        <option value="">Pilih jabatan</option>
                        <option value="Kepala Sekolah">Kepala Sekolah</option>
                        <option value="Staf Administrasi">Staf Administrasi</option>
                        <option value="Tim Penerimaan Siswa">Tim Penerimaan Siswa</option>
                        <option value="Pengelola Yayasan">Pengelola Yayasan</option>
                        <option value="Lainnya">Lainnya</option>
                      </select>
                    </Field>
                    <Field label="Email Sekolah" error={errors.email}>
                      <input name="email" type="email" value={form.email} onChange={updateField} className={inputClass} placeholder="nama@sekolah.sch.id" autoComplete="email" />
                    </Field>
                    <Field label="Nomor Telepon" error={errors.phone}>
                      <input name="phone" type="tel" value={form.phone} onChange={updateField} className={inputClass} placeholder="08xxxxxxxxxx" autoComplete="tel" />
                    </Field>
                    <Field label="Kata Sandi" error={errors.password}>
                      <div className="relative">
                        <input name="password" type={showPassword ? "text" : "password"} value={form.password} onChange={updateField} className={`${inputClass} pr-12`} placeholder="Minimal 8 karakter" autoComplete="new-password" />
                        <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-ink/55 hover:bg-cream focus-ring" aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}>
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </Field>
                    <Field label="Konfirmasi Kata Sandi" error={errors.confirmPassword}>
                      <input name="confirmPassword" type={showPassword ? "text" : "password"} value={form.confirmPassword} onChange={updateField} className={inputClass} placeholder="Ulangi kata sandi" autoComplete="new-password" />
                    </Field>
                  </div>

                  <Button type="submit" variant="yellow" className="mt-8 min-h-12 w-full">
                    Lanjutkan
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </form>
              ) : null}

              {!completed && step === 2 ? (
                <form onSubmit={handleNext} noValidate>
                  <div>
                    <p className="text-xs font-extrabold uppercase text-purple">Langkah 2 dari 3</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">Informasi Sekolah</h2>
                    <p className="mt-2 text-sm font-medium text-ink/60">Isi informasi dasar sekolah yang Anda wakili.</p>
                  </div>

                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Field label="Nama Sekolah" error={errors.schoolName}>
                        <input name="schoolName" value={form.schoolName} onChange={updateField} className={inputClass} placeholder="Masukkan nama sekolah" />
                      </Field>
                    </div>
                    <Field label="Jenjang" error={errors.schoolLevel}>
                      <select name="schoolLevel" value={form.schoolLevel} onChange={updateField} className={inputClass}>
                        <option value="">Pilih jenjang</option>
                        <option value="TK">TK</option>
                        <option value="SD">SD</option>
                        <option value="SMP">SMP</option>
                        <option value="SMA">SMA</option>
                        <option value="SMK">SMK</option>
                        <option value="Beberapa jenjang">Beberapa jenjang</option>
                      </select>
                    </Field>
                    <Field label="Kota / Kabupaten" error={errors.city}>
                      <input name="city" value={form.city} onChange={updateField} className={inputClass} placeholder="Contoh: Bandung" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Alamat Lengkap" error={errors.address}>
                        <textarea name="address" value={form.address} onChange={updateField} className={`${inputClass} min-h-[96px] resize-y`} placeholder="Masukkan alamat sekolah" />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Kurikulum" error={errors.curriculum}>
                        <input name="curriculum" value={form.curriculum} onChange={updateField} className={inputClass} placeholder="Contoh: Kurikulum Merdeka" />
                      </Field>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
                    <Button type="button" variant="secondary" className="min-h-12 sm:w-auto" onClick={() => setStep(1)}>
                      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                      Kembali
                    </Button>
                    <Button type="submit" variant="yellow" className="min-h-12 flex-1">
                      Tinjau Informasi
                      <ArrowRight className="h-5 w-5" aria-hidden="true" />
                    </Button>
                  </div>
                </form>
              ) : null}

              {!completed && step === 3 ? (
                <div>
                  <div>
                    <p className="text-xs font-extrabold uppercase text-purple">Langkah 3 dari 3</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">Review Pendaftaran</h2>
                    <p className="mt-2 text-sm font-medium text-ink/60">Pastikan informasi berikut sudah sesuai sebelum melanjutkan.</p>
                  </div>

                  <div className="mt-7 grid gap-4 md:grid-cols-2">
                    {reviewGroups.map((group) => (
                      <section key={group.title} className="rounded-[20px] border border-ink/10 bg-warm p-5">
                        <h3 className="flex items-center gap-2 font-extrabold text-ink">
                          <CheckCircle2 className="h-5 w-5 text-mint" aria-hidden="true" />
                          {group.title}
                        </h3>
                        <dl className="mt-3">
                          {group.items.map(([label, value]) => <SummaryRow key={label} label={label} value={value} />)}
                        </dl>
                      </section>
                    ))}
                  </div>

                  <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-[18px] border border-ink/10 bg-[#F2FBF8] p-4">
                    <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-purple" />
                    <span className="text-sm font-medium leading-relaxed text-ink/70">
                      Saya menyatakan informasi di atas telah ditinjau dan dapat digunakan untuk menyiapkan profil sekolah.
                    </span>
                  </label>

                  <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row">
                    <Button type="button" variant="secondary" className="min-h-12 sm:w-auto" onClick={() => setStep(2)}>
                      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                      Perbaiki Data
                    </Button>
                    <Button type="button" variant="yellow" disabled={!accepted} className="min-h-12 flex-1" onClick={finishRegistration}>
                      Selesaikan Pendaftaran
                      <ArrowRight className="h-5 w-5" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>

            {!completed ? (
              <div className="mt-8 border-t border-ink/10 pt-5 text-center text-xs font-medium leading-relaxed text-ink/50">
                <div className="flex items-start justify-center gap-2">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-mint" aria-hidden="true" />
                  Informasi digunakan untuk mendukung proses pendaftaran di MySchoolMate.
                </div>
                <p className="mt-3 text-sm text-ink/60">
                  Sudah punya akun?{" "}
                  <Link to="/login" className="font-bold text-purple underline-offset-4 hover:underline focus-ring">
                    Masuk
                  </Link>
                </p>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </main>
  );
}
